// API client for adventure admin endpoints.
//
// Mirrors the shape of src/lib/api.ts but scoped to adventures
// so the pages import a typed module rather than re-declaring
// the GET/POST shape inline.

import { api } from '../../../lib/api';
import type {
  Adventure,
  AdventureCommitmentTicket,
  AdventureGoal,
  AdventureWithGoals,
} from './types';

export interface AdventureLiveProgress {
  predictability_pct: number;
  committed_count: number;
  completed_count: number;
  starts_at: string;
  ends_at: string;
  goals: Array<{
    id: string;
    kind: 'tickets' | 'count';
    name: string;
    hit_when?: 'all_closed' | 'any_one_closed';
    target: number;
    progress: number;
    hit_now: boolean;
  }>;
}

export interface PredictabilityPoint {
  id: string;
  name: string;
  team_id: string;
  ends_at: string;
  predictability_pct: number | null;
  committed_count: number;
  completed_count: number;
}

export interface AdventureConfigSettings {
  predictable_threshold: number;
  coins_predictable: number;
  coins_goals_hit: number;
  coins_on_target: number;
  default_reward_pool_size: number;
  terminal_status_names: string[];
}

export interface AdventureListParams {
  team_id?: string;
  status?: Adventure['status'];
}

export const adventuresApi = {
  list(params: AdventureListParams = {}): Promise<{ adventures: Adventure[] }> {
    const q = new URLSearchParams();
    if (params.team_id) q.set('team_id', params.team_id);
    if (params.status) q.set('status', params.status);
    const qs = q.toString();
    return api(`/api/admin/adventures${qs ? `?${qs}` : ''}`);
  },

  // Phase 4: predictability trend. Closed adventures per team, ordered
  // by ends_at. Powers the list-page curve.
  predictabilityHistory(teamId?: string): Promise<{ points: PredictabilityPoint[] }> {
    return api(`/api/admin/adventures/predictability-history${teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''}`);
  },

  // Phase 7: per-team adventure scoring knobs.
  getConfig(teamId: string): Promise<{ settings: AdventureConfigSettings }> {
    return api(`/api/admin/teams/${teamId}/adventure-config`);
  },

  saveConfig(teamId: string, patch: Partial<AdventureConfigSettings>): Promise<{ settings: AdventureConfigSettings }> {
    return api(`/api/admin/teams/${teamId}/adventure-config`, { method: 'PATCH', body: patch });
  },

  get(id: string): Promise<{ adventure: AdventureWithGoals }> {
    return api(`/api/admin/adventures/${id}`);
  },

  create(input: {
    team_id: string;
    name: string;
    starts_at: string;
    ends_at: string;
    jira_sprint_id?: string | null;
    completion_grace_days?: number;
    commitment?: AdventureCommitmentTicket[];
    place?: string | null;
  }): Promise<{ adventure: Adventure }> {
    return api('/api/admin/adventures', {
      method: 'POST',
      body: input,
    });
  },

  update(
    id: string,
    patch: {
      name?: string;
      starts_at?: string;
      ends_at?: string;
      jira_sprint_id?: string | null;
      completion_grace_days?: number;
      commitment?: AdventureCommitmentTicket[];
    },
  ): Promise<{ adventure: Adventure }> {
    return api(`/api/admin/adventures/${id}`, {
      method: 'PATCH',
      body: patch,
    });
  },

  archive(id: string): Promise<{ archived: string }> {
    return api(`/api/admin/adventures/${id}`, { method: 'DELETE' });
  },

  // v0.8.9: hard delete. Same route but with ?hard=true. Api
  // refuses unless status is closed or archived — the ui
  // already gates the button to those statuses too, so the
  // 409 from the api should never fire in practice.
  hardDelete(id: string): Promise<{ deleted: string }> {
    return api(`/api/admin/adventures/${id}?hard=true`, { method: 'DELETE' });
  },

  // Phase 3: lifecycle transitions (ADR-0024).
  // start: draft → active, snapshots commitment.
  // end:   active → closed, computes scores + distributes coins.
  start(id: string): Promise<{ adventure: AdventureWithGoals; auto_closed_previous: string[] }> {
    return api(`/api/admin/adventures/${id}/start`, { method: 'POST' });
  },

  end(id: string): Promise<{ adventure: AdventureWithGoals }> {
    return api(`/api/admin/adventures/${id}/end`, { method: 'POST' });
  },

  // Phase 4: live progress (read-only, active adventure). Same
  // scoring formula as close but window end = NOW(), plus per-goal
  // progress. Polled while the adventure is active.
  progress(id: string): Promise<AdventureLiveProgress> {
    return api(`/api/admin/adventures/${id}/progress`);
  },

  // Phase 1.5: import-from-Jira helpers (ADR-0024).
  //
  // List the team's recent Jira sprints. The api calls Jira
  // server-side (the Jira API token never leaves the api).
  listJiraSprints(teamId: string): Promise<{ sprints: JiraSprintSummary[] }> {
    return api(`/api/admin/teams/${teamId}/jira-sprints`);
  },

  // Preview a specific sprint's commitment shape. Returns the
  // sprint metadata + the commitment list. Used by the
  // AdventureNewPage "Import from Jira" panel.
  previewFromJira(teamId: string, sprintId: string): Promise<JiraSprintPreview> {
    return api('/api/admin/adventures/preview-from-jira', {
      method: 'POST',
      body: { team_id: teamId, sprint_id: sprintId },
    });
  },

  // v0.8.9: live Jira sprint tickets for an adventure. Used by
  // the commitment-edit tab to render the full current sprint
  // (the commitment is the subset of these that the operator
  // checks). Returns { tickets: SprintTicket[] }. Empty tickets
  // means the team has no enabled Jira ingestor or the
  // adventure has no jira_sprint_id — the UI falls back to
  // the frozen commitment in that case.
  sprintTickets(id: string): Promise<{ tickets: SprintTicket[] }> {
    return api(`/api/admin/adventures/${id}/sprint-tickets`);
  },

  // v0.8.9: replace the commitment with the given ticket keys.
  // Validates each key against the current Jira sprint server-
  // side and returns the diff (added/removed) so the UI can
  // show a summary.
  updateCommitment(id: string, keys: string[]): Promise<{
    adventure: AdventureWithGoals;
    diff: { added: string[]; removed: string[]; total_after: number };
  }> {
    return api(`/api/admin/adventures/${id}/commitment`, {
      method: 'PATCH',
      body: { keys },
    });
  },

  // Phase 2 (ADR-0024): goal authoring. Goals are children of an
  // adventure; status='pending' until Phase 3 wires up close-time
  // scoring. The Shape (tickets vs count) is decided at create
  // time and can't change via PATCH.
  listGoals(adventureId: string): Promise<{ goals: AdventureGoal[] }> {
    return api(`/api/admin/adventures/${adventureId}/goals`);
  },

  // Live Jira sprint tickets for the goal picker. The adventure's
  // commitment is a frozen snapshot; this returns the CURRENT sprint
  // tickets so the operator can pick from tickets added mid-sprint.
  // Empty array when the adventure has no Jira link / no ingestor.
  getSprintTickets(adventureId: string): Promise<{ tickets: AdventureCommitmentTicket[] }> {
    return api(`/api/admin/adventures/${adventureId}/sprint-tickets`);
  },

  createGoal(
    adventureId: string,
    input:
      | { kind: 'tickets'; name: string; ticket_keys: string[]; hit_when?: 'all_closed' | 'any_one_closed' }
      | { kind: 'count'; name: string; metric_event_type: string; metric_target: number },
  ): Promise<{ goal: AdventureGoal }> {
    return api(`/api/admin/adventures/${adventureId}/goals`, {
      method: 'POST',
      body: input,
    });
  },

  updateGoal(
    adventureId: string,
    goalId: string,
    patch: {
      name?: string;
      position?: number;
      ticket_keys?: string[];
      hit_when?: 'all_closed' | 'any_one_closed';
      metric_event_type?: string;
      metric_target?: number;
    },
  ): Promise<{ goal: AdventureGoal }> {
    return api(`/api/admin/adventures/${adventureId}/goals/${goalId}`, {
      method: 'PATCH',
      body: patch,
    });
  },

  deleteGoal(adventureId: string, goalId: string): Promise<void> {
    return api(`/api/admin/adventures/${adventureId}/goals/${goalId}`, {
      method: 'DELETE',
    });
  },
};

export interface JiraSprintSummary {
  id: string;
  name: string;
  state: 'active' | 'closed' | 'future';
  start_date: string | null;
  end_date: string | null;
  complete_date: string | null;
  issue_count: number;
}

export interface JiraSprintPreview {
  sprint: JiraSprintSummary;
  tickets: SprintTicket[];
  grace_days_suggestion: number;
}

// Shape returned by GET /api/admin/adventures/:id/sprint-tickets.
// Used by the commitment-edit tab; the route returns the same
// shape as previewFromJira minus the sprint metadata wrapper.
export interface SprintTicket {
  key: string;
  type: string;
  summary: string;
  points_at_snapshot: number | null;
}
